/* eslint-disable max-len */
import { VoidCallback } from '@deqo/foundation';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loader from 'react-loader-spinner';
import { useInterval, useSessionStorage, useTimeoutFn } from 'react-use';
import styled from 'styled-components';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { LFC } from '../../../Legacy';
import { Check } from '../../../ui/Check';
import { Emoji } from '../../../ui/emoji';
import { getReturnLocation } from '../../common/ReturnLocation';
import { SoundEffect } from '../../common/SoundEffect';
import { useResolve } from '../../Container';
import { ButtonStrip, Copy, Em, Flexer, LinkButton, Spacer } from '../../dashboard/elements/LayoutElements';
import { Hangout } from '../../hangouts/Hangout';
import { useHangoutLocationObserver } from '../../hangouts/helpers/HangoutLocationObserver';
import { WaitingRoomEntry } from '../../packages/waitingRoom/WaitingRoomEntry';
import { ProfileModel } from '../../ProfileModel';
import { Box, Button, ButtonProps, Colors, remInPx } from './../../../ui';

const Top = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.text};

    ${Copy} {
        position: relative;
        font-size: 1.8rem;
        font-weight: 400;
        margin: 0;
    }
    
    svg {   
        margin-right: .5em;
    }
    
`;

const AnimateFrame = styled.div`
    position: relative;
    height: 4rem;
    & > * {
        position: absolute;
        top: 50%;
        width: 100%;
        transform: translateY(-50%);
    }
`;

const initialAnimation: Variants = {
    before: {
        opacity: 1,
        scale: 1.0,
    },
    out: {
        opacity: 0,
        scale: 0.5,
        transition: {
            duration: .2,
            ease: 'easeOut',
        }
    }
};

const nextAnimation: Variants = {
    before: {
        opacity: 0,
        y: '-2rem',
    },
    in: {
        opacity: 1,
        y: '0rem',
        transition: {
            duration: .2,
            ease: 'easeOut',
        }
    },
    out: {
        opacity: 0,
        scale: 0.5,
        transition: {
            duration: .2,
            ease: 'easeOut',
        }
    }
};

const InvokeAfterPause = React.memo<{ ms: number, callback: VoidCallback }>(({ ms, callback }) => {
    useTimeoutFn(callback, ms);
    return null;
});

const useHangoutTopic = (topic: string) => {
    const hangout = useResolve(Hangout);
    const handle = useMemo(() => hangout.subscribeTo(topic), []);
    useEffect(() => () => handle.destroy(), []);
    return handle;
};

const ButtonWithCooldown = React.memo<ButtonProps & { cooldown: number }>(({ cooldown, label, onAction, children, ...rest }) => {

    const [remaining, setRemaining] = useState(cooldown);

    useInterval(
        () => setRemaining(x => x - 1),
        remaining >= 0 ? 1000 : null,
    );

    const props = { ...rest, onAction, disabled: rest.disabled || remaining >= 0 };
    const text = remaining >= 0 ? `(${remaining}s)` : '';

    if (label) {
        return <Button {...props} label={`${label} ${text}`} />;
    }
    else {
        return <Button {...props}>
            {children} {text}
        </Button>;
    }
});

export type AskToJoinViewProps = { boardId: string, autoAsk?: boolean, callback: (result: 'allow' | 'refuse') => void };
export const AskToJoinView: LFC<AskToJoinViewProps> = ({ boardId, autoAsk, callback }) => {

    const me = useResolve(ProfileModel);
    const topic = useHangoutTopic(`board:${boardId}:waiting`);
    const [state, setState] = useState('initial' as 'initial' | 'asking' | 'allow' | 'refuse');
    const [cooldown, setCooldown] = useSessionStorage(`deqo/cooldowns/knock/${boardId}`, -2);

    // Determining if there is anyone that can let us in is kinda hard.  People with the ability to allow access (keepers) mark themselves
    // as in the waiting room with the state of "keeper".  However, if they disconnect they may leave this entry there as there isn't a
    // reliable way to clean it up.  So, we need to cross reference this list against the users who are in the board location (via the
    // hangout) so we can know who is really there to let us in.

    const entries = Object.values(useObserver(topic)) as WaitingRoomEntry[];
    const hangers = new Set(useHangoutLocationObserver(`*board:${boardId}`).map(x => x.id));
    const keepers = useMemo(() => entries.filter(x => x.state === 'keeping' && hangers.has(x.id)), [entries, hangers]);

    useEffect(() => {
        topic.receive('knockReply', (_sender, data) => {
            if (data.userId === me.id) {
                setState(x => x === 'asking' ? data.response : x);
            }
        });
    }, []);

    const onAsk = useCallback(async () => {
        // audio.play(Sounds.Knock);
        setCooldown(cooldown + 1);
        setState('asking');
        // Topic data is used to maintain waiting room state; pending requests are stored as a key of the user id and a value of all their
        // details with the state "waiting"
        const entry = WaitingRoomEntry(me, 'waiting');
        await topic.patch({ [entry.id]: entry });
        // We should also send a knock broadcast as it will cause the sound to play.  If the user refreshes to re-knock we want to play the
        // sound again which is why we do it on its own message rather than on the topic dat update.
        await topic.broadcast('knock');
    }, [cooldown]);

    useEffect(() => {
        if (autoAsk) {
            onAsk();
        }
    }, []);

    return <Flexer flex grow center>
        <Box flex="column" padding="1" width="32">
            <Top>
                <Icon size="2" src="deqo://icons/hero/lock-closed" />
                <Copy>
                    Protected Board
                </Copy>
            </Top>
            <Spacer f={1} />
            <Copy size="1.125">
                <span>You need permission to join this board. &nbsp;</span>
                {!!keepers.length && <span>There are currently <Em>{keepers.length} people in the board</Em> that can let you join...</span>}
                {!keepers.length && <span>There is nobody here at the moment. <Em>Ask the person that shared the link with you to join and let you in.</Em></span>}
            </Copy>
            <Spacer f={1} />
            {<AnimateFrame>
                <AnimatePresence>
                    {state === 'initial' && <Box>
                        <motion.div variants={initialAnimation} initial="before" exit="out">
                            <ButtonStrip align="center">
                                <ButtonWithCooldown cooldown={Math.min(cooldown * 15, 60)} size="xl" variant="primary" label="Ask to join" disabled={!keepers.length} onAction={onAsk} />
                                <LinkButton to={getReturnLocation() || '~/'} size="xl" label="Go back" />
                            </ButtonStrip>
                        </motion.div>
                    </Box>}
                </AnimatePresence>
                <AnimatePresence>
                    {state === 'asking' && <Box>
                        <motion.div variants={nextAnimation} initial="before" animate="in" exit="out">
                            <Flexer flex="row" center>
                                <Loader
                                    type="TailSpin"
                                    color={Colors.text}
                                    height={remInPx(2)}
                                    width={remInPx(2)}
                                />
                                <Box padding="0 1">
                                    <Copy flush size="1.5">Asking to join...</Copy>
                                </Box>
                            </Flexer>
                        </motion.div>
                    </Box>}
                </AnimatePresence>
                <AnimatePresence>
                    {state === 'allow' && <Box>
                        <SoundEffect name="KnockSuccess" />
                        <motion.div variants={nextAnimation} initial="before" animate="in" exit="out">
                            <Flexer flex="row" center>
                                <Check value={true} />
                                <Box padding="0 1">
                                    <Copy flush size="1.5">You&apos;re all set!</Copy>
                                </Box>
                            </Flexer>
                        </motion.div>
                    </Box>}
                </AnimatePresence>
                <AnimatePresence>
                    {state === 'refuse' && <Box>
                        <SoundEffect name="KnockFail" />
                        <motion.div variants={nextAnimation} initial="before" animate="in" exit="out">
                            <Flexer flex="row" center>
                                <Copy flush size="1.5">
                                    <Emoji idOrCode=":thumbdown:" /> You were not allowed in...
                                </Copy>
                            </Flexer>
                        </motion.div>
                    </Box>}
                </AnimatePresence>
            </AnimateFrame>}
            {(state === 'allow' || state === 'refuse') && (
                <InvokeAfterPause ms={2500} callback={() => callback(state)} />
            )}
        </Box>
    </Flexer>;
};
